import {
  Badge,
  Box,
  Center,
  Pagination,
  Paper,
  Table,
  Text,
  Title,
  UnstyledButton,
} from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import React, { useState } from 'react';
import { ArrowLeft, X } from 'tabler-icons-react';
import { formatDateWithTime } from '../../helpers/date-convert';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import useDidMountEffect from '../../hooks/useDidMountEffect';
import { IPaginationMeta } from '../../type/admin.types';
import { ErrorResponse } from '../../type/error-response.types';
import {
  BasicTransactionResponseDetails,
  RetrieveAllTransactionsRequest,
} from '../../type/retrieve-transaction.types';
import ExportButton from '../admin-transactions/export-button.component';

import { Currencies } from '../../enums/currencies.enum';
import './merchant-transactions.styles.css';

const tableCols = {
  transactionId: 'Transaction ID',
  transactionNumber: 'Transaction number',
  merchantName: 'Company name',
  branchName: 'Branch name',
  posName: 'POS',
  customerName: 'Customer name',
  cardNumber: 'Card number',
  amount: 'Amount',
  dateCreated: 'Date created',
  signatureSource: 'Signature',
  posEntryMode: 'POS entry mode',
  notes: 'Notes',
  checkoutTransaction: 'Product Code/s',
};
interface Props {
  transactionDetails: BasicTransactionResponseDetails[] | undefined;
  detailsPaginationData: IPaginationMeta | undefined;
  transactionDetailsRequest: RetrieveAllTransactionsRequest | undefined;
  setShowTransactionSummary: React.Dispatch<React.SetStateAction<boolean>>;
}

function MerchantTransactionsDetails(props: Props) {
  const {
    transactionDetails,
    detailsPaginationData,
    transactionDetailsRequest,
    setShowTransactionSummary,
  } = props;
  const [paginationData] = useState(detailsPaginationData);
  const [page, setPage] = useState(1);
  const [data, setData] = useState(transactionDetails);
  const axiosPrivate = useAxiosPrivate();

  useDidMountEffect(() => {
    let ignore = false;
    const retrieveNextPage = async () => {
      try {
        const request: RetrieveAllTransactionsRequest = {
          ...transactionDetailsRequest!,
          page: page,
        };
        const response = await axiosPrivate.post('/web/transactions', request);
        const items: BasicTransactionResponseDetails[] = response.data.items;
        if (!ignore) {
          setData(items);
        }
      } catch (err: any) {
        const error: ErrorResponse = err.response?.data;

        showNotification({
          id: 'failed-to-retrieve-next-pagination-data-details-branch',
          message: error.error || 'An Unexpected Error Occurred',
          icon: <X size={16} />,
          color: 'red',
          autoClose: 10000,
        });
      }
    };

    retrieveNextPage();

    return () => {
      ignore = true;
    };
  }, [page]);

  return (
    <>
      <UnstyledButton
        color='dimmed'
        onClick={() => {
          setShowTransactionSummary(true);
        }}
        mt='lg'
        data-cy='backButton'
      >
        <Center inline>
          <ArrowLeft size={12} color='#868e96' />
          <Box ml={5}>
            <Text size='sm' color='dimmed'>
              Back
            </Text>
          </Box>
        </Center>
      </UnstyledButton>

      <Title order={2} my='lg'>
        Transaction Details
      </Title>
      <div style={{ flex: 1, margin: 0 }}>
        <Paper
          withBorder
          radius='md'
          shadow='lg'
          style={{
            flex: 1,
            height: '100%',
            padding: '20px 0',
            overflow: 'auto',
          }}
        >
          <Box p='md'>
            {transactionDetailsRequest && (
              <ExportButton
                transactionDetailsRequest={transactionDetailsRequest}
              />
            )}
          </Box>
          <div className='transaction-table admin-table'>
            <Table
              highlightOnHover
              sx={{ '& thead tr th': { height: 50 } }}
              horizontalSpacing='md'
            >
              <thead style={{ fontSize: '11px' }}>
                <tr>
                  {Object.keys(tableCols).map((key: string) => (
                    <th key={key}>
                      {tableCols[key as keyof typeof tableCols]}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data?.map((row, index) => (
                  <tr key={index}>
                    {Object.keys(tableCols).map((key: string) => {
                      return (
                        <td key={key}>
                          {!row[key as keyof typeof row] ||
                          row[key as keyof typeof row] === 'N/A' ||
                          row[key as keyof typeof row] === '.' ? (
                            <Text color='dimmed' style={{ fontSize: '14px' }}>
                              N/A
                            </Text>
                          ) : key === 'amount' ? (
                            Object.values(Currencies)[
                              transactionDetailsRequest?.detailedFilters[0]
                                .currencyId! - 1
                            ] +
                            ' ' +
                            row.amount.toFixed(2)
                          ) : key === 'dateCreated' ? (
                            formatDateWithTime(row.dateCreated.toString())
                          ) : key === 'checkoutTransaction' &&
                            row.checkoutTransaction !== undefined ? (
                            row.checkoutTransaction.items.map((product) => {
                              return <Badge mr={3}>{product.code}</Badge>;
                            })
                          ) : (
                            row[key as keyof typeof row]?.toString()
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </Table>
            {data && data.length > 0 && (
              <Pagination
                total={paginationData?.totalPages || 1}
                onChange={(value: number) => setPage(value)}
                page={page}
                position='center'
                p='md'
                spacing='md'
              />
            )}
          </div>
        </Paper>
      </div>
    </>
  );
}

export default MerchantTransactionsDetails;
